import { useLazyQuery } from "@apollo/react-hooks";
import { Grid, useMediaQuery } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { navigate } from "@reach/router";
import { DefaultInfo } from "components/NothingRight";
import Table from "components/Table";
import { ROW_PER_PAGE_OPTIONS } from "config";
import { theme } from "config/theme";
import useText from "constants/text";
import Filter from "domains/CashManagement/components/dialogs/Filter";
import ParcelModal from "domains/CashManagement/components/dialogs/ParcelModal";
import { Header } from "domains/CashManagement/components/Header";
import { RecapCard } from "domains/CashManagement/components/RecapCard";
import { remittanceActiveCardIdentifier, usRemittanceColums } from "domains/CashManagement/data";
import { RemittanceLayout, useRemittanceStyles } from "domains/CashManagement/style";
import { defaultFilterValues, FilterForm } from "domains/CashManagement/types";
import {
  ParcelRecapStatus,
  Remittance as IRemittance,
  Remittance as RemittanceProps,
  RemittanceBadge,
  RemittancesBadge,
} from "generated/graphql";
import { GET_REMITTANCE_RECAP } from "graphql/cashManagement/queries";
import { useCurrentClient } from "hooks";
import { Slider } from "components/Slider/Slider";
import React, { FC } from "react";
import { isSuccess } from "utils/https";
import { RemittanceDetail } from "../detailPages/RemittanceDetail";

// eslint-disable-next-line arrow-body-style
export const Remittance: FC = (props) => {
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [cashQuery, setCashQuery] = React.useState<FilterForm>(defaultFilterValues);
  const [remittanceRecap, setRemittanceRecap] = React.useState<RemittancesBadge[]>([]);
  const [countTotal, setCountTotal] = React.useState<number>();
  const { client } = useCurrentClient();
  const [remittanceStatus, setRemittanceStatus] = React.useState<string>(ParcelRecapStatus.RemittanceOnGoing);
  const [skeleton, setSkeleton] = React.useState(Array.from(new Array(3)));
  const [remittances, setRemittances] = React.useState<RemittanceProps[]>([]);
  const [parcelIds, setParcelIds] = React.useState<string[]>([]);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const { text } = useText();
  const classes = useRemittanceStyles();
  const [activeCard, setActiveCard] = React.useState<number>(0);
  const [hasFilters, setHasFilters] = React.useState<boolean>(false);
  const [open, setOpen] = React.useState<boolean>(false);
  const [openParcelModal, setOpenParcelModal] = React.useState<boolean>(false);
  const [isSubmetted, setIsSubmetted] = React.useState<boolean>(false);
  const [remittanceUid, setRemittanceUid] = React.useState<string>();
  const [remittanceDetails, setRemittanceDetails] = React.useState<RemittanceProps | any>(null);
  const [openSlider, setOpenSlider] = React.useState(false);

  const openModal = React.useCallback((parcels: string[], remittanceUid: string): void => {
    setOpenParcelModal(true);
    setParcelIds(parcels);
    setRemittanceUid(remittanceUid);
  }, []);

  const columns = usRemittanceColums(text, openModal);

  const [getRemittanceCash, { loading }] = useLazyQuery(GET_REMITTANCE_RECAP, {
    onCompleted: (data) => {
      if (isSuccess(data)) {
        const badges = data?.results?.data?.badges?.filter(
          (badge: any) => badge.status !== ParcelRecapStatus.RemittanceInHouse
        );
        setRemittanceRecap(badges);
        setRemittances(data?.results?.data?.remittances);
        setCountTotal(data?.results?.data?.remittances?.length);
      }
    },

    fetchPolicy: "cache-and-network",
  });

  const handleReset = (activeStatus?: string) => {
    setCashQuery(defaultFilterValues);
    getRemittanceCash({
      variables: {
        queries: {
          client_id: client?._id,
          status: remittanceStatus,
        },
      },
    });
    setRemittanceStatus(activeStatus || remittanceStatus);
    setActiveCard(remittanceActiveCardIdentifier[activeStatus]);
  };

  const handleClick = (FilterStatus: string) => {
    setPage(0);
    setRemittanceDetails(null);
    setRemittanceStatus(FilterStatus);
    navigate(`/cash/remittance/${FilterStatus.toLowerCase()}`);
    setActiveCard(remittanceActiveCardIdentifier[FilterStatus]);
  };

  const onConfirm = () => {
    getRemittanceCash({
      variables: {
        queries: {
          client_id: client?._id,
          status: remittanceStatus,
          from_date: cashQuery?.from,
          to_date: cashQuery?.to,
        },
      },
    });
    setIsSubmetted(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const onCloseParcelModal = () => {
    setOpenParcelModal(false);
  };

  const handleChangePage = (event: React.ChangeEvent<HTMLInputElement>, newpage: number) => {
    setPage(newpage);
  };

  const remittancesByTab = React.useMemo(() => {
    const undeclinedRemittances: IRemittance[] = remittances?.filter(
      (remittance: IRemittance) => remittance?.badge !== RemittanceBadge.NotReceived
    );
    const declinedRemittances: IRemittance[] = remittances?.filter(
      (remittance: IRemittance) => remittance?.badge === RemittanceBadge.NotReceived
    );
    return remittanceStatus !== ParcelRecapStatus.NotReceived ? undeclinedRemittances : declinedRemittances;
  }, [remittances, remittanceStatus]);

  React.useEffect(() => {
    getRemittanceCash({
      variables: {
        queries: {
          client_id: client?._id,
          status: remittanceStatus,
          from_date: cashQuery?.from,
          to_date: cashQuery?.to,
          page: page + 1,
          limit: rowsPerPage,
        },
      },
    });
  }, [client, activeCard, isSubmetted, remittanceStatus]);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const handleOnRowClick = (row: RemittanceProps) => {
    if (isSmallScreen) {
      setRemittanceDetails(row);
      setOpenSlider(true);
    } else {
      setRemittanceDetails(row);
    }
  };

  return (
    <RemittanceLayout>
      <div className="left">
        <div className="container">
          <Header
            setSearchTerm={setSearchTerm}
            title="Cash Remittance"
            clearFilter={handleReset}
            searchTerm={searchTerm}
            buttonClick={setOpen}
            cashQuery={cashQuery}
          />
          <Grid className="card-container">
            {loading && !remittanceRecap?.length
              ? skeleton?.map(() => <Skeleton className="skeleton" width="auto" height={200} animation="wave" />)
              : remittanceRecap?.map(({ count, status, amount }: RemittancesBadge, index: number) => (
                  <RecapCard
                    key={index}
                    amount={amount}
                    count={count}
                    status={status}
                    clickAction={() => handleClick(status)}
                    activeCard={activeCard}
                    identifier={index}
                    isRmittance
                  />
                ))}
          </Grid>
          <div className={classes.table}>
            <Table
              tableHeight="49vh"
              searchTerm={searchTerm}
              rowsIdentifier="_id"
              columns={columns}
              data={remittancesByTab}
              isLoading={loading}
              rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
              rowsPerPage={rowsPerPage}
              currentPage={page}
              countTotal={countTotal}
              setRowsPerPage={setRowsPerPage}
              onRowClick={handleOnRowClick}
              onChangePage={handleChangePage}
            />
          </div>
        </div>
      </div>

      {!isSmallScreen &&
        (remittanceDetails ? (
          <div className={`${"right"} ${remittanceDetails && "animate-history"}`}>
            <RemittanceDetail
              setRemittanceDetails={setRemittanceDetails}
              setActiveCard={setActiveCard}
              setRemittanceStatus={setRemittanceStatus}
              remittance={remittanceDetails}
            />
          </div>
        ) : (
          <div className="right">
            <DefaultInfo />
          </div>
        ))}

      {isSmallScreen && (
        <Slider openDrawer={openSlider} handleClose={() => setOpenSlider(false)}>
          <div className={`${"right"} ${remittanceDetails && "animate-history"}`}>
            <RemittanceDetail
              setRemittanceDetails={setRemittanceDetails}
              setActiveCard={setActiveCard}
              setRemittanceStatus={setRemittanceStatus}
              remittance={remittanceDetails}
            />
          </div>
        </Slider>
      )}

      <Filter
        open={open}
        onClose={onClose}
        onConfirm={onConfirm}
        setHasFilters={setHasFilters}
        cashQuery={cashQuery}
        setCashQuery={setCashQuery}
      />

      <ParcelModal
        open={openParcelModal}
        onClose={onCloseParcelModal}
        onConfirm={onConfirm}
        parcelIds={parcelIds}
        remittanceUid={remittanceUid}
      />
    </RemittanceLayout>
  );
};
